import React, { useState, useCallback } from 'react';
import { supabase } from '../supabase/client';
import { useNavigate } from 'react-router-dom';
import { debounce } from 'lodash';
import CompanyVerificationPending from '../components/profile/CompanyVerificationPending';

interface ProfileFormData {
  full_name: string;
  user_display_id: string;
  company_name: string;
  join_existing_company: boolean;
  whatsapp_number: string;
  line_id: string;
}

interface CompanySuggestion {
  id: string;
  name: string;
  member_count: number;
  subscription_tier: string;
  allows_joining: boolean;
}

const CompleteProfile: React.FC = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [verificationRequired, setVerificationRequired] = useState(false);
  const [verificationInfo, setVerificationInfo] = useState<{
    company_name: string;
    message: string;
  } | null>(null);
  
  const [formData, setFormData] = useState<ProfileFormData>({
    full_name: '',
    user_display_id: '',
    company_name: '',
    join_existing_company: false,
    whatsapp_number: '',
    line_id: ''
  });

  const [companySuggestions, setCompanySuggestions] = useState<CompanySuggestion[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [userIdAvailable, setUserIdAvailable] = useState<boolean | null>(null);
  const [userIdSuggestions, setUserIdSuggestions] = useState<string[]>([]);

  // Company search with debouncing
  const debouncedSearchCompanies = debounce(async (query: string) => {
    if (query.length >= 2) {
      try {
        const response = await supabase.functions.invoke('searchCompanies', {
          body: { query, limit: 5 }
        });
        
        if (response.data?.success) {
          setCompanySuggestions(response.data.companies);
        }
      } catch (error) {
        console.error('Error searching companies:', error);
      }
    } else {
      setCompanySuggestions([]);
    }
  }, 300);
  
  const searchCompanies = useCallback((query: string) => {
    debouncedSearchCompanies(query);
  }, [debouncedSearchCompanies]);

  // User ID availability check
  const debouncedCheckUserIdAvailability = debounce(async (userId: string) => {
    if (userId.length >= 3) {
      try {
        const response = await supabase.functions.invoke('checkUserIdAvailability', {
          body: { user_display_id: userId }
        });
        
        setUserIdAvailable(response.data?.available || false);
        if (!response.data?.available && response.data?.suggestions) {
          setUserIdSuggestions(response.data.suggestions);
        } else {
          setUserIdSuggestions([]);
        }
      } catch {
        setUserIdAvailable(null);
        setUserIdSuggestions([]);
      }
    }
  }, 500);
  
  const checkUserIdAvailability = useCallback((userId: string) => {
    debouncedCheckUserIdAvailability(userId);
  }, [debouncedCheckUserIdAvailability]);

  const handleInputChange = (field: keyof ProfileFormData, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    if (field === 'company_name') {
      searchCompanies(value);
      setFormData(prev => ({ ...prev, join_existing_company: false }));
    }
    
    if (field === 'user_display_id') {
      checkUserIdAvailability(value);
    }
  };

  const handleCompanySelect = (company: CompanySuggestion) => {
    setFormData(prev => ({
      ...prev,
      company_name: company.name,
      join_existing_company: true
    }));
    setCompanySuggestions([]);
  };

  const handleUserIdSuggestionSelect = (suggestion: string) => {
    setFormData(prev => ({ ...prev, user_display_id: suggestion }));
    setUserIdAvailable(true);
    setUserIdSuggestions([]);
  };

  const validateStep = (step: number): boolean => {
    const stepErrors: string[] = [];

    if (step === 1) {
      if (!formData.full_name.trim()) stepErrors.push('Full name is required');
      if (!formData.user_display_id.trim()) stepErrors.push('User ID is required');
      if (userIdAvailable === false) stepErrors.push('User ID is already taken');
    }

    if (step === 2) {
      if (!formData.company_name.trim()) stepErrors.push('Company name is required');
    }

    setErrors(stepErrors);
    return stepErrors.length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 2));
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) return;

    console.log('CompleteProfile: Starting submission with data:', formData);
    setIsSubmitting(true);
    try {
      console.log('CompleteProfile: Calling completeUserProfile function...');
      const response = await supabase.functions.invoke('completeUserProfile', {
        body: formData
      });

      console.log('CompleteProfile: Function response:', response);

      if (response.error) {
        console.error('CompleteProfile: Function error:', response.error);
        throw response.error;
      }

      console.log('CompleteProfile: Function data:', response.data);

      if (response.data?.verification_required) {
        console.log('CompleteProfile: Verification required, showing pending screen');
        setVerificationRequired(true);
        setVerificationInfo(response.data.verification_required);
      } else {
        console.log('CompleteProfile: Profile completed successfully, navigating to dashboard');
        navigate('/dashboard');
      }
    } catch (error: unknown) {
      console.error('CompleteProfile: Error during submission:', error);
      const errorMessage = error instanceof Error ? error.message : 'Profile completion failed';
      setErrors([errorMessage]);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (verificationRequired) {
    return <CompanyVerificationPending verificationInfo={verificationInfo} />;
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          Complete Your Profile
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Step {currentStep} of 2
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {/* Progress bar */}
          <div className="mb-6">
            <div className="flex items-center">
              {[1, 2].map((step) => (
                <div key={step} className="flex-1 flex items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    step <= currentStep
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-300 text-gray-500'
                  }`}>
                    {step}
                  </div>
                  {step < 3 && (
                    <div className={`flex-1 h-1 mx-2 ${
                      step < currentStep ? 'bg-blue-600' : 'bg-gray-300'
                    }`} />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Error display */}
          {errors.length > 0 && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">
                    Please fix the following errors:
                  </h3>
                  <div className="mt-2 text-sm text-red-700">
                    <ul className="list-disc list-inside space-y-1">
                      {errors.map((error, index) => (
                        <li key={index}>{error}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 1: Personal Information */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <div>
                <label htmlFor="full_name" className="block text-sm font-medium text-gray-700">
                  Full Name *
                </label>
                <div className="mt-1">
                  <input
                    id="full_name"
                    name="full_name"
                    type="text"
                    required
                    value={formData.full_name}
                    onChange={(e) => handleInputChange('full_name', e.target.value)}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder="Enter your full name"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="user_display_id" className="block text-sm font-medium text-gray-700">
                  User ID *
                </label>
                <div className="mt-1 relative">
                  <input
                    id="user_display_id"
                    name="user_display_id"
                    type="text"
                    required
                    value={formData.user_display_id}
                    onChange={(e) => handleInputChange('user_display_id', e.target.value)}
                    className={`appearance-none block w-full px-3 py-2 border rounded-md placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
                      userIdAvailable === false ? 'border-red-300' : 
                      userIdAvailable === true ? 'border-green-300' : 'border-gray-300'
                    }`}
                    placeholder="Choose a unique user ID"
                  />
                  {userIdAvailable !== null && (
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                      {userIdAvailable ? (
                        <svg className="h-5 w-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      ) : (
                        <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                  )}
                </div>
                <p className="mt-2 text-sm text-gray-500">
                  3-20 characters, letters, numbers, underscores, and hyphens only
                </p>
                
                {/* User ID Suggestions */}
                {userIdSuggestions.length > 0 && (
                  <div className="mt-2">
                    <p className="text-sm text-gray-700 mb-2">Suggestions:</p>
                    <div className="flex flex-wrap gap-2">
                      {userIdSuggestions.map((suggestion) => (
                        <button
                          key={suggestion}
                          type="button"
                          onClick={() => handleUserIdSuggestionSelect(suggestion)}
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 hover:bg-blue-200 cursor-pointer"
                        >
                          {suggestion}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 2: Company Information */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <div>
                <label htmlFor="company_name" className="block text-sm font-medium text-gray-700">
                  Company Name *
                </label>
                <div className="mt-1 relative">
                  <input
                    id="company_name"
                    name="company_name"
                    type="text"
                    required
                    value={formData.company_name}
                    onChange={(e) => handleInputChange('company_name', e.target.value)}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder="Enter your company name"
                  />
                  
                  {/* Company suggestions dropdown */}
                  {companySuggestions.length > 0 && (
                    <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                      {companySuggestions.map((company) => (
                        <div
                          key={company.id}
                          onClick={() => handleCompanySelect(company)}
                          className="cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-blue-50"
                        >
                          <div className="flex items-center">
                            <span className="font-normal block truncate">
                              {company.name}
                            </span>
                            <span className="text-gray-500 ml-2 text-sm">
                              ({company.member_count} member{company.member_count !== 1 ? 's' : ''})
                            </span>
                          </div>
                          <div className="text-xs text-gray-400">
                            {company.subscription_tier} • 
                            {company.allows_joining ? ' Open to join' : ' Requires approval'}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                
                {formData.join_existing_company && (
                  <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-md">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-blue-800">
                          Joining Existing Company
                        </h3>
                        <div className="mt-2 text-sm text-blue-700">
                          <p>
                            You'll need approval from existing company members. 
                            We'll send them a verification email.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                <p className="mt-2 text-sm text-gray-500">
                  Start typing to search existing companies or create a new one
                </p>
              </div>

              {/* Optional Communication Methods */}
              <div className="mt-6 space-y-4">
                <h3 className="text-sm font-medium text-gray-700">
                  Optional Contact Information
                </h3>
                
                <div>
                  <label htmlFor="whatsapp_number" className="block text-sm font-medium text-gray-700">
                    WhatsApp Number (Optional)
                  </label>
                  <div className="mt-1">
                    <input
                      id="whatsapp_number"
                      name="whatsapp_number"
                      type="tel"
                      value={formData.whatsapp_number}
                      onChange={(e) => handleInputChange('whatsapp_number', e.target.value)}
                      className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      placeholder="+1234567890"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="line_id" className="block text-sm font-medium text-gray-700">
                    Line ID (Optional)
                  </label>
                  <div className="mt-1">
                    <input
                      id="line_id"
                      name="line_id"
                      type="text"
                      value={formData.line_id}
                      onChange={(e) => handleInputChange('line_id', e.target.value)}
                      className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      placeholder="Your Line ID"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}


          {/* Navigation buttons */}
          <div className="mt-6 flex justify-between">
            <button
              type="button"
              onClick={handlePrevious}
              disabled={currentStep === 1}
              className={`px-4 py-2 text-sm font-medium rounded-md ${
                currentStep === 1
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
              }`}
            >
              Previous
            </button>

            <button
              type="button"
              onClick={currentStep === 2 ? handleSubmit : handleNext}
              disabled={isSubmitting}
              className="px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Processing...' : currentStep === 2 ? 'Complete Profile' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompleteProfile;